import { Fragment, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../common/Mypage.scss';
import '../../../recruit/common/Recruit.scss';
import axios from 'axios';
import MainURL from '../../../../MainURL';
import ImageURL from '../../../../ImageURL';
import Loading from '../../../../components/Loading';
import MypageMenu from '../../common/MypageMenu';
import type { ResumeSnapshot } from '../../../../types/application';
import { churchApi, errorMessage, type ChurchDetail } from '../../applications/applicationStatusApi';
import { MessageThread, StatusBadge, Timeline } from '../../applications/applicationUi';
import ChurchStatusActions from '../../applications/ChurchStatusActions';

// 교회용 지원서 상세 — 지원 시점 스냅샷 + 상태 변경 + 진행 기록 + 메시지
// 최초 열람 시 서버가 '열람됨'으로 기록한다 (구직자에게 알림 없음). 실명·연락처는 '연락' 이후 공개.
export default function ApplicantResume() {
  const navigate = useNavigate();
  const location = useLocation();
  const applicationId = new URLSearchParams(location.search).get('id') || '';

  const [application, setApplication] = useState<ChurchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!applicationId) return;
    try {
      setApplication(await churchApi.detail(applicationId));
      setError('');
    } catch (e) {
      setError(errorMessage(e, '지원서를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const resume = application?.resumeSnapshot as (ResumeSnapshot & { channel?: string; message?: string; attachmentNames?: string[] }) | undefined;

  // 파일 지원 첨부 — 토큰 인증이 필요해 링크 대신 받아서 저장
  const downloadAttachment = async (index: number, fileName: string) => {
    try {
      const res = await axios.get(`${MainURL}/applications/church/application/${applicationId}/file/${index}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      alert('파일을 내려받지 못했습니다.');
    }
  };
  const birth = resume ? [resume.birthYear, resume.birthMonth, resume.birthDay].filter(Boolean).join('.') : '';

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
              <Loading />
            </div>
          ) : !application || !resume ? (
            <div className="mypage-panel mypage-section-card">
              <div className="noPosts">
                <p>{error || '지원서를 찾을 수 없습니다.'}</p>
              </div>
            </div>
          ) : (
            <div className="mypage-panel mypage-section-card">
              <section style={{ marginBottom: '30px' }}>
                <div className="main_title_row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h3 className="main_title">{resume.name} 님의 지원서</h3>
                    <StatusBadge status={application.status} />
                  </div>
                  <div className="list-btn" onClick={() => navigate(-1)}>
                    <span>목록으로</span>
                  </div>
                </div>
                <p style={{ color: 'var(--ink-4)', fontSize: '16px' }}>
                  {application.job.title} · 지원일 {application.appliedAt}
                  {application.contactRevealedAt ? ` · 연락처 공개 ${application.contactRevealedAt}` : ''}
                </p>
                <p style={{ color: 'var(--ink-4)', fontSize: '15px', marginTop: 4 }}>지원 시점 이력서 (지원 이후 수정 내용은 반영되지 않습니다)</p>
                <div className="app-board-row__buttons" style={{ marginTop: 14 }}>
                  <ChurchStatusActions
                    applicationId={application.id}
                    version={application.version}
                    actions={application.actions}
                    applicantName={resume.name}
                    onChanged={load}
                  />
                </div>
                {!application.applicant.contact && (
                  <p style={{ color: 'var(--ink-4)', fontSize: '15px', marginTop: 8 }}>실명·연락처는 '연락'을 누르면 공개됩니다.</p>
                )}
              </section>

              <section className="app-detail">
                <h1 className="section_title">진행 기록</h1>
                <Timeline items={application.timeline} />
              </section>

              {(application.actions.includes('MESSAGE') || application.messages.length > 0) && (
                <section className="app-detail">
                  <h1 className="section_title">메시지</h1>
                  <MessageThread
                    applicationId={application.id}
                    messages={application.messages}
                    canSend={application.actions.includes('MESSAGE')}
                    onSent={(m) => setApplication({ ...application, messages: [...application.messages, m] })}
                  />
                </section>
              )}

              {resume.channel === 'FILE' && (
                <section className="app-detail">
                  <h1 className="section_title">첨부 이력서</h1>
                  <p style={{ color: 'var(--ink-4)', fontSize: '15px', marginBottom: 10 }}>
                    이력서 파일로 지원했습니다. 지원자가 교회 제공에 동의한 파일입니다.
                  </p>
                  {resume.message && <p style={{ whiteSpace: 'pre-line', fontSize: 16, marginBottom: 10 }}>{resume.message}</p>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(resume.attachmentNames || []).map((fileName, index) => (
                      <div key={fileName + index} className="list-btn" onClick={() => downloadAttachment(index, fileName)}>
                        <span>{fileName}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h1 className="section_title">기본 정보</h1>
                <div className="copInfoBx">
                  <div className="copInfoBx-cover">
                    {resume.profileImage && (
                      <div className="copLogo">
                        <img
                          className="churchLogo"
                          src={`${ImageURL}/images/resume/profile/${resume.profileImage}`}
                          alt="프로필"
                          style={{ objectFit: 'cover' }}
                        />
                      </div>
                    )}
                    <div className="copInfo">
                      <div className="label">이력서 제목</div>
                      <div className="value">{resume.title || '-'}</div>
                      <div className="label">이름</div>
                      <div className="value">{resume.name || '-'}</div>
                      <div className="label">성별</div>
                      <div className="value">{resume.gender || '-'}</div>
                      <div className="label">생년월일</div>
                      <div className="value">{birth || '-'}{resume.ageText ? ` (만 ${resume.ageText}세)` : ''}</div>
                      <div className="label">휴대폰</div>
                      <div className="value">{resume.phone || (application.applicant.contact ? '-' : '연락 이후 공개')}</div>
                      <div className="label">이메일</div>
                      <div className="value">{resume.email || (application.applicant.contact ? '-' : '연락 이후 공개')}</div>
                      <div className="label">주소</div>
                      <div className="value">{[resume.address, resume.addressDetail].filter(Boolean).join(' ') || '-'}</div>
                    </div>
                  </div>
                </div>
              </section>

              {resume.educationItems.length > 0 && (
                <section>
                  <h1 className="section_title">학력</h1>
                  <div className="copInfoBx">
                    <div className="copInfoBx-cover">
                      <div className="copInfo">
                        {resume.educationItems.map((item, index) => (
                          <Fragment key={`edu-${index}`}>
                            <div className="label">{item.schoolName || `학력 ${index + 1}`}</div>
                            <div className="value">
                              {item.major && `${item.major} · `}{item.startDate}~{item.endDate} {item.status && `(${item.status})`}
                            </div>
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {resume.careerItems.length > 0 && (
                <section>
                  <h1 className="section_title">경력</h1>
                  <div className="copInfoBx">
                    <div className="copInfoBx-cover">
                      <div className="copInfo">
                        {resume.careerItems.map((item, index) => (
                          <Fragment key={`career-${index}`}>
                            <div className="label">{item.companyName || `경력 ${index + 1}`}</div>
                            <div className="value">
                              {item.employType && `[${item.employType}] `}{item.position && `${item.position} · `}{item.startDate}~{item.endDate}
                              {item.description && ` — ${item.description}`}
                            </div>
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section>
                <h1 className="section_title">희망사항</h1>
                <div className="copInfoBx">
                  <div className="copInfoBx-cover">
                    <div className="copInfo">
                      <div className="label">희망 유형</div>
                      <div className="value">{resume.hopeType || '-'}</div>
                      <div className="label">희망 지역</div>
                      <div className="value">{resume.hopeLocation || '-'}</div>
                      <div className="label">희망 급여</div>
                      <div className="value">{resume.hopeSalaryPaySort === '면접 후 협의' ? '면접 후 협의' : resume.hopeSalary ? `${resume.hopeSalaryPaySort} ${resume.hopeSalary}만원` : '-'}</div>
                      <div className="label">희망 역할</div>
                      <div className="value">{resume.hopeRoles || '-'}</div>
                      <div className="label">희망 부서</div>
                      <div className="value">{resume.hopeDepartment || '-'}</div>
                      <div className="label">희망 보험</div>
                      <div className="value">{resume.hopeInsurance || '-'}</div>
                      <div className="label">희망 복리후생</div>
                      <div className="value">{resume.hopeWelfare || '-'}</div>
                    </div>
                  </div>
                </div>
              </section>

              <div className="bottom-list-btn-wrap">
                <div className="bottom-list-btn-inner">
                  <div className="list-btn list-btn--bottom" onClick={() => navigate(-1)}>
                    <span>목록으로</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
